import { useEffect, useMemo, useState } from "react";
import { parseISO, isBefore } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { Link } from "react-router-dom";
import { Anchor, Calendar as CalendarIcon, ChevronDown, ChevronRight, ExternalLink, Heart, MapPin, Repeat, Search, Star, Ticket, UtensilsCrossed, X } from "lucide-react";

import { api } from "@/lib/api";
import useFavorites from "@/lib/useFavorites";
import EventsSchema from "@/components/EventsSchema";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import FeaturedStrip from "./_FeaturedStrip";

// Southern Maine — everything shown/filtered by America/New_York calendar day
// (handles EDT/EST automatically). We deliberately model each "day" as a plain
// YYYY-MM-DD string to avoid Date-object timezone bugs in the browser.
const APP_TZ = "America/New_York";

// Popular filter pills (must match slugs in backend services/tag_filters.py).
const TAG_PILLS = [
  { slug: "live-music",   label: "Live Music" },
  { slug: "karaoke",      label: "Karaoke" },
  { slug: "open-mic",     label: "Open Mic" },
  { slug: "comedy",       label: "Comedy" },
  { slug: "happy-hour",   label: "Happy Hour" },
  { slug: "kid-friendly", label: "Kid Friendly" },
  { slug: "community",    label: "Community" },
  { slug: "trivia",       label: "Trivia" },
  { slug: "food-drink",   label: "Food & Drink" },
  { slug: "outdoor",      label: "Outdoor" },
  { slug: "art",          label: "Art" },
  { slug: "sports",       label: "Sports" },
];

// Current NY calendar day as "YYYY-MM-DD"
function ymdNow() {
  return formatInTimeZone(new Date(), APP_TZ, "yyyy-MM-dd");
}

// Anchor a YYYY-MM-DD at UTC noon so any browser TZ resolves it back to the
// same NY calendar day (avoids DST midnight ambiguity in November).
function ymdToAnchor(ymd) {
  return new Date(`${ymd}T12:00:00Z`);
}

function addDaysYmd(ymd, n) {
  const d = ymdToAnchor(ymd);
  d.setUTCDate(d.getUTCDate() + n);
  return formatInTimeZone(d, APP_TZ, "yyyy-MM-dd");
}

function highlight(text, q) {
  if (!q || !text) return text;
  const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig");
  const parts = String(text).split(re);
  return parts.map((p, i) => {
    // Keys combine the actual chunk text with the index — chunks never reorder
    // because they come from a stable split of the source string, so this is safe.
    const key = `${i}-${p}`;
    return re.test(p)
      ? <mark key={key} className="smq-hit">{p}</mark>
      : <span key={key}>{p}</span>;
  });
}

function fmtTime(iso) {
  try { return formatInTimeZone(parseISO(iso), APP_TZ, "h:mm a"); } catch { return ""; }
}

/**
 * Classify an event's time so the row can render "All Day", a multi-day range,
 * or a plain start time.
 *
 *  - All Day: starts at 00:00 Eastern, and (no end OR spans >=20h)
 *  - Multi-day: end date is on a later calendar day than start (>=30h span)
 *  - Regular: everything else — just show the start time
 */
function timeDisplay(startIso, endIso) {
  try {
    const start = parseISO(startIso);
    const end = endIso ? parseISO(endIso) : null;
    const startDayNY = formatInTimeZone(start, APP_TZ, "yyyy-MM-dd");
    const startHourNY = Number(formatInTimeZone(start, APP_TZ, "HH"));
    const startMinNY = Number(formatInTimeZone(start, APP_TZ, "mm"));
    const spanH = end ? (end - start) / 3_600_000 : 0;

    // Multi-day: covers 2+ calendar days (30h+ span)
    if (end && spanH >= 30) {
      const endDayNY = formatInTimeZone(end, APP_TZ, "yyyy-MM-dd");
      if (endDayNY !== startDayNY) {
        return {
          kind: "range",
          primary: formatInTimeZone(start, APP_TZ, "MMM d"),
          secondary: formatInTimeZone(end, APP_TZ, "MMM d"),
        };
      }
    }
    // All-day: 00:00 start + (no end OR ~24h duration)
    if (startHourNY === 0 && startMinNY === 0 && (!end || spanH >= 20)) {
      return { kind: "allday", primary: "All Day" };
    }
    return { kind: "time", primary: formatInTimeZone(start, APP_TZ, "h:mm a") };
  } catch {
    return { kind: "time", primary: "" };
  }
}

export function renderEventRow(e, { past, catColor, q, isFavorite, toggleFav }) {
  const color = catColor(e.category);
  const td = timeDisplay(e.start_datetime, e.end_datetime);
  const fav = isFavorite ? isFavorite(e.id) : false;
  return (
    <a
      key={e.id}
      href={e.source_url || "#"}
      target={e.source_url ? "_blank" : undefined}
      rel="noopener noreferrer"
      data-testid={`event-row-${e.id}`}
      className={`smq-row block py-3 hover:bg-white/70 ${past ? "opacity-60" : ""}`}
    >
      <div className="grid grid-cols-[64px_1fr_auto] sm:grid-cols-[80px_1fr_auto] gap-3 items-start">
        <div className={`font-display font-semibold text-sm text-[#1A3C5A] tabular-nums ${past ? "line-through" : ""}`}>
          {td.kind === "range" ? (
            <div className="leading-tight" data-testid={`event-range-${e.id}`}>
              <div className="text-xs">{td.primary}</div>
              <div className="text-[10px] text-slate-500 font-normal">→ {td.secondary}</div>
            </div>
          ) : td.kind === "allday" ? (
            <span className="text-xs uppercase tracking-wide font-bold text-[#B45309]" data-testid={`event-allday-${e.id}`}>
              All Day
            </span>
          ) : (
            td.primary
          )}
        </div>
        <div className="min-w-0">
          <div className={`font-display font-semibold text-[15px] leading-snug text-[#1E293B] ${past ? "line-through" : ""}`}>
            {e.featured && (
              <Star size={11} strokeWidth={2.5} className="inline -mt-0.5 mr-1 fill-amber-400 text-amber-500" />
            )}
            {highlight(e.title, q)}
            {e.source_url && (
              <ExternalLink size={12} className="inline ml-1 mb-0.5 text-slate-400" strokeWidth={2} />
            )}
          </div>
          <div className="text-[13px] text-slate-600 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            {e.venue && <span>{highlight(e.venue, q)}</span>}
            {e.town && (
              <span className="inline-flex items-center gap-0.5 text-slate-500">
                <MapPin size={11} strokeWidth={2} /> {highlight(e.town, q)}
              </span>
            )}
            {e.category && (
              <span
                className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
                style={{ backgroundColor: color }}
              >
                {e.category}
              </span>
            )}
            {e.is_paid && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#B45309] bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                <Ticket size={11} strokeWidth={2} /> Paid
              </span>
            )}
            {e.is_recurring && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 border border-slate-300 rounded-full px-2 py-0.5" title={e.recurrence_rule || "recurring"}>
                <Repeat size={11} strokeWidth={2} /> Series
              </span>
            )}
          </div>
        </div>
        {toggleFav && (
          <button
            type="button"
            onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); toggleFav(e.id); }}
            aria-label={fav ? "Remove from favorites" : "Save to favorites"}
            aria-pressed={fav}
            data-testid={`fav-btn-${e.id}`}
            className={`self-center p-1.5 rounded-full transition-colors ${fav ? "text-rose-500 hover:bg-rose-50" : "text-slate-300 hover:text-rose-400 hover:bg-slate-50"}`}
          >
            <Heart size={16} strokeWidth={2.25} className={fav ? "fill-rose-500" : ""} />
          </button>
        )}
      </div>
    </a>
  );
}

export default function PublicHome() {
  // `today`, `selectedDate`, and each entry in `days` are YYYY-MM-DD strings
  // representing calendar days in America/New_York.
  const today = useMemo(() => ymdNow(), []);
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDaysYmd(today, i)),
    [today]
  );

  const [selectedDate, setSelectedDate] = useState(today);
  const [town, setTown] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [tags, setTags] = useState(() => new Set());
  const [towns, setTowns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [events, setEvents] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const { isFavorite, toggle: toggleFav, count: favCount } = useFavorites();

  useEffect(() => {
    api.get("/public/towns").then(r => setTowns(r.data)).catch(() => {});
    api.get("/public/categories").then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const tagsKey = useMemo(() => Array.from(tags).sort().join(","), [tags]);

  // Re-fetch featured events whenever filters change so the strip mirrors what
  // the visitor has selected (town / category / pills / search). Hidden when
  // the Featured pill itself is on (main list already shows featured).
  useEffect(() => {
    if (featuredOnly) { setFeatured([]); return; }
    const params = { limit: 12 };
    if (town) params.town = town;
    if (category) params.category = category;
    if (q) params.q = q;
    if (tagsKey) params.tags = tagsKey;
    api.get("/public/featured", { params })
      .then(r => setFeatured(r.data))
      .catch(() => setFeatured([]));
  }, [town, category, q, tagsKey, featuredOnly]);

  useEffect(() => {
    setLoading(true);
    // selectedDate is already a YYYY-MM-DD string interpreted server-side as
    // a calendar day in America/New_York.
    const params = featuredOnly ? { featured: true } : { day: selectedDate };
    if (town) params.town = town;
    if (category) params.category = category;
    if (q) params.q = q;
    if (tagsKey) params.tags = tagsKey;
    api.get("/public/events", { params })
      .then(r => setEvents(r.data))
      .finally(() => setLoading(false));
  }, [selectedDate, town, category, q, tagsKey, featuredOnly]);

  // Reset the "show past" toggle every time the day changes or filters change
  useEffect(() => { setShowPast(false); }, [selectedDate, town, category, q, tagsKey, featuredOnly]);

  const clearAll = () => { setTown(""); setCategory(""); setQ(""); setTags(new Set()); setSelectedDate(today); setFeaturedOnly(false); };
  const anyFilter = town || category || q || tags.size > 0 || (selectedDate !== today) || featuredOnly;
  const isToday = !featuredOnly && selectedDate === today;

  const togglePill = (slug) => {
    setTags(prev => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  // Split into past + upcoming ONLY on the Today tab.
  // An event is "past" if it has definitively ENDED — use end_datetime when
  // present so "all day" happy hours don't get pushed to "earlier" at 12:01 AM.
  const { pastEvents, upcomingEvents } = useMemo(() => {
    if (!isToday) return { pastEvents: [], upcomingEvents: events };
    const now = new Date();
    const past = [], upcoming = [];
    for (const e of events) {
      const endRef = e.end_datetime ? parseISO(e.end_datetime) : parseISO(e.start_datetime);
      (isBefore(endRef, now) ? past : upcoming).push(e);
    }
    return { pastEvents: past, upcomingEvents: upcoming };
  }, [events, isToday]);

  const catColor = (name) => categories.find(c => c.name === name)?.color || "#334155";

  return (
    <div className="grain-bg min-h-screen">
      {/* SEO: JSON-LD ItemList of events for Google Rich Results */}
      <EventsSchema events={events} />
      <div className="relative z-10 max-w-[820px] mx-auto px-4 pb-24">
        {/* Header */}
        <header className="pt-8 pb-4 flex items-baseline gap-3 border-b border-border">
          <Anchor className="text-[#1A3C5A]" size={22} strokeWidth={2} />
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#1A3C5A]"
              data-testid="site-title">
            Southern Maine Events
          </h1>
          <Link
            to="/favorites"
            data-testid="nav-favorites"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-rose-500 text-rose-600 hover:bg-rose-50 font-display font-semibold text-xs uppercase tracking-wider transition-colors"
          >
            <Heart size={13} strokeWidth={2.25} className={favCount > 0 ? "fill-rose-500" : ""} />
            {favCount > 0 ? favCount : ""} Favorites
          </Link>
          <Link
            to="/submit-event"
            data-testid="nav-submit-event"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-700 text-emerald-700 hover:bg-emerald-50 font-display font-semibold text-xs uppercase tracking-wider transition-colors"
          >
            + Submit Event
          </Link>
          <a
            href="https://mainefoodtrucktracker.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-food-trucks"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#B45309] text-[#B45309] hover:bg-amber-50 font-display font-semibold text-xs uppercase tracking-wider transition-colors"
          >
            <UtensilsCrossed size={13} strokeWidth={2.25} /> Food Trucks
          </a>
        </header>

        {/* Featured strip — editorial hero above the day tabs. Mirrors filters
            (town/category/pills/search) so it reflects what the visitor picked.
            Variant switch: "editorial" for the un-filtered homepage (visually
            striking hero+brick), "carousel" once any filter is applied (uniform
            look regardless of how many results). */}
        {featured.length > 0 && (
          <div className="pt-6">
            <FeaturedStrip
              events={featured}
              variant={(town || category || q || tags.size > 0) ? "carousel" : "editorial"}
              contextLabel={(() => {
                const bits = [];
                if (category) bits.push(category);
                if (town) bits.push(`in ${town}`);
                if (tags.size === 1) {
                  const label = TAG_PILLS.find(p => p.slug === Array.from(tags)[0])?.label;
                  if (label) bits.push(label);
                }
                return bits.length ? `· ${bits.join(" ")}` : "";
              })()}
            />
          </div>
        )}

        {/* Day tabs — hidden when the Featured pill is active (results span all dates) */}
        {!featuredOnly && (
        <div
          className="sticky top-0 z-20 bg-[#F5F6F5]/90 backdrop-blur-md border-b border-border py-3 -mx-4 px-4 flex gap-2 overflow-x-auto no-scrollbar"
          data-testid="day-tabs"
        >
          {days.map((d, idx) => {
            const active = d === selectedDate;
            const label = idx === 0 ? "TODAY" : formatInTimeZone(ymdToAnchor(d), APP_TZ, "EEE").toUpperCase();
            return (
              <button
                key={d}
                onClick={() => setSelectedDate(d)}
                data-testid={`day-tab-${idx}`}
                className={`smq-tab shrink-0 px-3 py-1.5 rounded-md font-display text-sm font-semibold border ${
                  active
                    ? "bg-[#1A3C5A] text-white border-[#1A3C5A]"
                    : "bg-white text-[#1E293B] border-border hover:border-[#1A3C5A]"
                }`}
              >
                <div className="leading-none">{label}</div>
                <div className={`text-[10px] mt-0.5 ${active ? "text-white/70" : "text-slate-500"}`}>
                  {formatInTimeZone(ymdToAnchor(d), APP_TZ, "MMM d")}
                </div>
              </button>
            );
          })}
          {/* Date picker (jump beyond 7 days) */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="smq-tab shrink-0 px-3 py-1.5 rounded-md font-display text-sm font-semibold border bg-white border-border hover:border-[#1A3C5A] flex items-center gap-1.5"
                data-testid="date-picker-trigger"
              >
                <CalendarIcon size={14} strokeWidth={2} />
                Pick date
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0">
              <Calendar
                mode="single"
                selected={ymdToAnchor(selectedDate)}
                onSelect={(d) => {
                  if (!d) return;
                  // Interpret the picked date by its local calendar components
                  // (which is what the user clicked on), independent of browser TZ.
                  const y = d.getFullYear();
                  const m = String(d.getMonth() + 1).padStart(2, "0");
                  const day = String(d.getDate()).padStart(2, "0");
                  setSelectedDate(`${y}-${m}-${day}`);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        )}

        {/* Quick filter pills (multi-select) */}
        <div
          className="pt-3 flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4"
          data-testid="tag-pills"
        >
          <button
            key="featured"
            onClick={() => setFeaturedOnly(v => !v)}
            data-testid="pill-featured"
            aria-pressed={featuredOnly}
            className={`shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-display font-semibold border transition-all ${
              featuredOnly
                ? "bg-amber-400 text-amber-950 border-amber-500 shadow-sm"
                : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"
            }`}
          >
            <Star size={11} strokeWidth={2.5} className={featuredOnly ? "fill-amber-950" : "fill-amber-400 text-amber-500"} />
            Featured
          </button>
          {TAG_PILLS.map(({ slug, label }) => {
            const active = tags.has(slug);
            return (
              <button
                key={slug}
                onClick={() => togglePill(slug)}
                data-testid={`pill-${slug}`}
                aria-pressed={active}
                className={`shrink-0 px-3 py-1 rounded-full text-xs font-display font-semibold border transition-all ${
                  active
                    ? "bg-[#B45309] text-white border-[#B45309] shadow-sm"
                    : "bg-white text-slate-600 border-border hover:border-[#B45309] hover:text-[#B45309]"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 pt-4 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={2} />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search title, venue, town…"
              className="pl-9 h-10 bg-white"
              data-testid="search-input"
            />
          </div>
          <Select value={town || "__all"} onValueChange={(v) => setTown(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-[160px] h-10 bg-white" data-testid="town-filter">
              <SelectValue placeholder="All towns" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All towns</SelectItem>
              {towns.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={category || "__all"} onValueChange={(v) => setCategory(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-[170px] h-10 bg-white" data-testid="category-filter">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All categories</SelectItem>
              {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {anyFilter && (
            <Button
              variant="ghost"
              onClick={clearAll}
              className="h-10 gap-1.5 text-slate-600 hover:text-[#1A3C5A]"
              data-testid="clear-filters-btn"
            >
              <X size={14} /> Clear
            </Button>
          )}
        </div>

        {/* Count */}
        <div className="pt-4 pb-2 flex items-baseline justify-between border-b border-border">
          <div className="font-display text-lg font-semibold tracking-tight text-[#1E293B]" data-testid="event-count">
            {loading
              ? "…"
              : isToday && pastEvents.length > 0
                ? <><span>{upcomingEvents.length} upcoming</span><span className="text-slate-400 font-normal"> · {pastEvents.length} earlier</span></>
                : `${events.length} event${events.length === 1 ? "" : "s"}`}
          </div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-display">
            {featuredOnly
              ? <span className="inline-flex items-center gap-1 text-amber-700"><Star size={11} className="fill-amber-400 text-amber-500" strokeWidth={2}/> All upcoming featured</span>
              : formatInTimeZone(ymdToAnchor(selectedDate), APP_TZ, "EEEE · MMMM d")}
          </div>
        </div>

        {/* Event rows */}
        <div data-testid="event-list">
          {events.length === 0 && !loading && (
            <div className="py-16 text-center text-slate-500">
              <div className="font-display text-lg">No events yet for this day.</div>
              <div className="text-sm mt-1">Try clearing filters or picking another date.</div>
            </div>
          )}

          {/* Collapsible "earlier today" section — only on the Today tab, only if there are past events */}
          {isToday && pastEvents.length > 0 && (
            <div className="border-b border-border">
              <button
                type="button"
                onClick={() => setShowPast(v => !v)}
                aria-expanded={showPast}
                data-testid="toggle-past-events"
                className="smq-row w-full flex items-center gap-2 py-2.5 text-sm text-slate-500 hover:text-[#1A3C5A]"
              >
                {showPast ? <ChevronDown size={14} strokeWidth={2.25} /> : <ChevronRight size={14} strokeWidth={2.25} />}
                <span className="font-display font-medium">
                  {pastEvents.length} event{pastEvents.length === 1 ? "" : "s"} earlier today
                </span>
                <span className="text-xs text-slate-400">
                  {showPast ? "hide" : "show"}
                </span>
              </button>
              {showPast && (
                <div className="divide-y divide-border pb-1" data-testid="past-events-list">
                  {pastEvents.map((e) => renderEventRow(e, { past: true, catColor, q, isFavorite, toggleFav }))}
                </div>
              )}
            </div>
          )}

          {/* Upcoming (or all, on non-Today tabs) */}
          <div className="divide-y divide-border">
            {upcomingEvents.map((e) => renderEventRow(e, { past: false, catColor, q, isFavorite, toggleFav }))}
            {isToday && upcomingEvents.length === 0 && pastEvents.length > 0 && (
              <div className="py-12 text-center text-slate-500">
                <div className="font-display">All of today&apos;s events have already started.</div>
                <div className="text-sm mt-1">
                  {showPast
                    ? "See the list above."
                    : (
                      <button className="underline hover:text-[#1A3C5A]" onClick={() => setShowPast(true)}>
                        Show {pastEvents.length} earlier event{pastEvents.length === 1 ? "" : "s"}
                      </button>
                    )}
                </div>
              </div>
            )}
          </div>
        </div>

        <footer className="pt-16 text-xs text-slate-500 font-display uppercase tracking-widest">
          Curated for the coast · <a href="/admin/login" className="hover:text-[#1A3C5A]" data-testid="admin-link">Admin</a>
        </footer>
      </div>
    </div>
  );
}
